import React, { useCallback, useEffect, useRef, useState } from 'react';
import IconText from '../icons/IconText';
import { ICON } from '../../icons/iconIds';
import SelkirkIcon from '../icons/SelkirkIcon';
import Card from '../ui/Card';
import Button from '../ui/Button';
import Spinner from '../ui/Spinner';
import { documentDesignService } from '../../services/documentDesignService';
import FolderTree from './FolderTree';
import MediaGrid from './MediaGrid';
import MediaDetailPanel from './MediaDetailPanel';
import MediaUploadModal from './MediaUploadModal';
import { COVER_READY_FILTER } from './slotFitness';

const PAGE_SIZE = 60;

const EMPTY_FILTERS = {
  q: '',
  search: 'semantic',
  folderPath: '',
  orientation: '',
  approved: false,
  coverReady: false,
  flagged: false,
};

function toQuery(filters, offset) {
  const [category, ...rest] = filters.folderPath ? filters.folderPath.split('/') : [];
  return {
    q: filters.q.trim(),
    search: filters.q.trim() ? filters.search : undefined,
    category,
    subcategory: rest.length ? rest.join('/') : undefined,
    orientation: filters.coverReady ? COVER_READY_FILTER.orientation : filters.orientation,
    min_width: filters.coverReady ? COVER_READY_FILTER.min_width : undefined,
    min_height: filters.coverReady ? COVER_READY_FILTER.min_height : undefined,
    approved: filters.approved,
    flagged: filters.flagged,
    limit: PAGE_SIZE,
    offset,
  };
}

export function formatRelative(iso) {
  if (!iso) return 'never';
  const minutes = Math.round((Date.now() - new Date(iso).getTime()) / 60000);
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes} min ago`;
  if (minutes < 1440) return `${Math.round(minutes / 60)} h ago`;
  return new Date(iso).toLocaleDateString();
}

const BrandLibraryTab = () => {
  const [status, setStatus] = useState(null);
  const [folders, setFolders] = useState([]);
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [queryText, setQueryText] = useState('');
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [searchMode, setSearchMode] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(null);
  const [selected, setSelected] = useState(null);
  const [showUpload, setShowUpload] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [syncMessage, setSyncMessage] = useState(null);
  const requestSeq = useRef(0);

  const loadLibrary = useCallback(async () => {
    try {
      const [statusData, folderData] = await Promise.all([
        documentDesignService.getStatus(),
        documentDesignService.getFolders(),
      ]);
      setStatus(statusData);
      setFolders(folderData);
    } catch (err) {
      console.error('❌ Error loading Brand Library status:', err);
      setError(err.message || 'Failed to load Brand Library');
    }
  }, []);

  const loadMedia = useCallback(async (activeFilters, offset = 0) => {
    const seq = ++requestSeq.current;
    if (offset === 0) setLoading(true);
    else setLoadingMore(true);
    try {
      const result = await documentDesignService.listMedia(toQuery(activeFilters, offset));
      if (seq !== requestSeq.current) return;
      setItems((prev) => (offset === 0 ? result.items : [...prev, ...result.items]));
      setTotal(result.total);
      setSearchMode(result.searchMode);
      setError(null);
    } catch (err) {
      if (seq !== requestSeq.current) return;
      console.error('❌ Error loading Brand Library media:', err);
      setError(err.message || 'Failed to load photos');
    } finally {
      if (seq === requestSeq.current) {
        setLoading(false);
        setLoadingMore(false);
      }
    }
  }, []);

  useEffect(() => {
    loadLibrary();
  }, [loadLibrary]);

  useEffect(() => {
    loadMedia(filters, 0);
  }, [filters, loadMedia]);

  useEffect(() => {
    const handle = setTimeout(() => {
      setFilters((f) => (f.q === queryText ? f : { ...f, q: queryText }));
    }, 400);
    return () => clearTimeout(handle);
  }, [queryText]);

  const setFilter = (key, value) => setFilters((f) => ({ ...f, [key]: value }));

  const refreshAll = () => {
    loadLibrary();
    loadMedia(filters, 0);
  };

  const runSync = async () => {
    setSyncing(true);
    setSyncMessage(null);
    try {
      const stats = await documentDesignService.runSync();
      if (stats.skipped) {
        setSyncMessage(`Sync skipped: ${stats.skipped}`);
      } else {
        const parts = [
          `${stats.files_changed} changed`,
          `${stats.ingested} processed`,
          stats.enriched != null ? `${stats.enriched} tagged` : null,
          stats.errors ? `${stats.errors} errors` : null,
          stats.budget_exhausted ? 'more remaining — runs again on schedule' : null,
        ].filter(Boolean);
        setSyncMessage(`Sync complete: ${parts.join(', ')}`);
      }
      refreshAll();
    } catch (err) {
      setSyncMessage(`Sync failed: ${err.message}`);
    } finally {
      setSyncing(false);
    }
  };

  const handleUpdated = (updated) => {
    setItems((prev) => prev.map((it) => (it.id === updated.id ? { ...it, ...updated } : it)));
  };

  const handleDeleted = (id, result) => {
    setItems((prev) => prev.filter((it) => it.id !== id));
    setTotal((t) => Math.max(0, (t || 0) - 1));
    setSelected(null);
    setSyncMessage(
      result?.sharepoint_deleted === false
        ? `Removed ${result.file_name} from the library (it was already gone from SharePoint).`
        : `Deleted ${result?.file_name || 'file'} — it is in the SharePoint recycle bin if you need it back.`,
    );
    loadLibrary();
  };

  const counts = status?.counts || {};
  const sync = status?.sync;
  const setupIssues = status
    ? [
        !status.graph_configured && 'SharePoint sync credentials (BRAND_LIBRARY_CLIENT_ID / BRAND_LIBRARY_CLIENT_SECRET) are not set on the Function App.',
        !status.storage_configured && 'Blob storage connection string is not set on the Function App.',
        !status.ai_configured && 'OpenAI key is not set; photos will sync without AI tags.',
        sync?.last_error && `Last sync error: ${sync.last_error}`,
      ].filter(Boolean)
    : [];
  const hasFilters = JSON.stringify({ ...filters, q: queryText }) !== JSON.stringify(EMPTY_FILTERS);

  return (
    <>
      <div className="dd-tab-actions">
        <p className="dd-muted">Curated photos for report covers, section openers and layouts</p>
        <div className="dd-header-actions">
          {status?.library_url && (
            <a className="dd-link-button" href={status.library_url} target="_blank" rel="noreferrer">
              Open in SharePoint ↗
            </a>
          )}
          <Button variant="secondary" onClick={runSync} loading={syncing} disabled={!status?.graph_configured}>
            Sync now
          </Button>
          <Button onClick={() => setShowUpload(true)} disabled={!status?.graph_configured || folders.length === 0}>
            Upload photos
          </Button>
        </div>
      </div>

      <Card className="summary-card">
        <div className="summary-grid">
          <div className="stat-item">
            <span className="stat-label">Photos</span>
            <span className="stat-value">{counts.ready ?? '—'}</span>
          </div>
          <div className="stat-item">
            <span className="stat-label">AI tagged</span>
            <span className="stat-value">{counts.enriched ?? '—'}</span>
          </div>
          <div className="stat-item">
            <span className="stat-label">Approved external</span>
            <span className="stat-value">{counts.approved_external ?? '—'}</span>
          </div>
          <div className="stat-item">
            <span className="stat-label">Queued</span>
            <span className="stat-value">{(counts.pending || 0) + (counts.enrich_pending || 0)}</span>
          </div>
          <div className="stat-item">
            <span className="stat-label">Last sync</span>
            <span className="stat-value status-ready">{formatRelative(sync?.last_sync_completed_at)}</span>
          </div>
        </div>
      </Card>

      {setupIssues.length > 0 && (
        <Card className="dd-setup-card">
          <h3>Setup needed</h3>
          <ul>
            {setupIssues.map((msg) => (
              <li key={msg}>{msg}</li>
            ))}
          </ul>
        </Card>
      )}
      {syncMessage && <div className="dd-sync-message">{syncMessage}</div>}

      <div className={`dd-library ${selected ? 'with-detail' : ''}`}>
        <Card className="dd-sidebar-card">
          <h3>Folders</h3>
          <FolderTree folders={folders} selectedPath={filters.folderPath} onSelect={(p) => setFilter('folderPath', p)} />
        </Card>

        <div className="dd-main">
          <Card className="controls-card">
            <div className="dd-toolbar">
              <input
                type="search"
                className="dd-search"
                placeholder='Search photos, e.g. "aerial of the mill in winter"'
                value={queryText}
                onChange={(e) => setQueryText(e.target.value)}
              />
              <select
                value={filters.search}
                onChange={(e) => setFilter('search', e.target.value)}
                title="Semantic search matches meaning; text search matches words"
              >
                <option value="semantic">By meaning</option>
                <option value="text">By keyword</option>
              </select>
              <select
                value={filters.orientation}
                onChange={(e) => setFilter('orientation', e.target.value)}
                disabled={filters.coverReady}
              >
                <option value="">Any orientation</option>
                <option value="landscape">Landscape</option>
                <option value="portrait">Portrait</option>
                <option value="square">Square</option>
              </select>
            </div>
            <div className="dd-chip-row dd-filter-chips">
              <button type="button" className={`dd-filter-chip ${filters.approved ? 'on' : ''}`} onClick={() => setFilter('approved', !filters.approved)}>
                <IconText icon={ICON.uiCheckOn} iconSize={14}>Approved external</IconText>
              </button>
              <button type="button" className={`dd-filter-chip ${filters.coverReady ? 'on' : ''}`} onClick={() => setFilter('coverReady', !filters.coverReady)}>
                Cover-ready (portrait, 300 dpi)
              </button>
              <button type="button" className={`dd-filter-chip ${filters.flagged ? 'on' : ''}`} onClick={() => setFilter('flagged', !filters.flagged)}>
                <IconText icon={ICON.uiWarning} iconSize={14}>Needs review</IconText>
              </button>
              {hasFilters && (
                <button type="button" className="dd-filter-chip clear" onClick={() => { setQueryText(''); setFilters(EMPTY_FILTERS); }}>
                  Clear filters
                </button>
              )}
            </div>
          </Card>

          <Card className="content-card">
            <div className="dd-results-header">
              <h2>
                {filters.folderPath || 'All photos'}
                <span className="dd-muted"> · {total} {total === 1 ? 'photo' : 'photos'}</span>
              </h2>
              {searchMode === 'semantic' && <span className="dd-muted">Ranked by relevance</span>}
            </div>

            {error && (
              <div className="dd-error-inline">
                {error} <Button size="sm" variant="ghost" onClick={refreshAll}>Retry</Button>
              </div>
            )}

            {loading ? (
              <div className="loading-container">
                <Spinner size="large" />
                <p>Loading photos…</p>
              </div>
            ) : items.length === 0 ? (
              <div className="no-data">
                <div className="no-data-icon"><SelkirkIcon name={ICON.ddTabBrandLibrary} size={48} decorative /></div>
                <h3>{hasFilters ? 'No photos match these filters' : 'The Brand Library is empty'}</h3>
                <p>
                  {hasFilters
                    ? 'Try a different search or clear the filters.'
                    : 'Add photos to the SharePoint Brand Library or use Upload photos. New files appear after the next sync (every 15 minutes).'}
                </p>
              </div>
            ) : (
              <>
                <MediaGrid items={items} selectedId={selected?.id} onSelect={setSelected} />
                {items.length < total && (
                  <div className="dd-load-more">
                    <Button variant="secondary" onClick={() => loadMedia(filters, items.length)} loading={loadingMore}>
                      Load more ({total - items.length} remaining)
                    </Button>
                  </div>
                )}
              </>
            )}
          </Card>
        </div>

        {selected && (
          <MediaDetailPanel
            item={selected}
            onClose={() => setSelected(null)}
            onUpdated={handleUpdated}
            onDeleted={handleDeleted}
          />
        )}
      </div>

      {showUpload && (
        <MediaUploadModal
          folders={folders}
          defaultFolder={filters.folderPath}
          onClose={() => setShowUpload(false)}
          onUploaded={refreshAll}
        />
      )}
    </>
  );
};

export default BrandLibraryTab;
